import { useBranding } from "@/hooks/useBranding"
import {
  Box,
  Button,
  Flex,
  HStack,
  Image,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { type ReactNode, useEffect, useState } from "react"

export type LoadingSplashProps = {
  heading?: ReactNode
  message?: ReactNode
  isError?: boolean
  onRetry?: () => void
}

const AUTO_REFRESH_SECONDS = 10

function refreshPage() {
  window.location.reload()
}

export function LoadingSplash({
  heading,
  message = "Preparing your workspace...",
  isError = false,
  onRetry,
}: LoadingSplashProps) {
  const { display_name, logo_url, isLoading: brandingLoading } = useBranding()
  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_REFRESH_SECONDS)

  useEffect(() => {
    const startedAt = Date.now()
    const tick = () => {
      const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000)
      setSecondsRemaining(Math.max(0, AUTO_REFRESH_SECONDS - elapsedSeconds))
    }

    tick()
    const intervalId = window.setInterval(tick, 250)
    const timeoutId = window.setTimeout(
      refreshPage,
      AUTO_REFRESH_SECONDS * 1000,
    )

    return () => {
      window.clearInterval(intervalId)
      window.clearTimeout(timeoutId)
    }
  }, [])

  // Show minimal loader while branding assets are not yet known
  if (brandingLoading) {
    return (
      <Flex
        position="fixed"
        top={0}
        right={0}
        bottom={0}
        left={0}
        bg="bg.canvas"
        align="center"
        justify="center"
        zIndex="modal"
      >
        <Spinner thickness="4px" speed="0.75s" color="teal.500" size="xl" />
      </Flex>
    )
  }

  return (
    <Flex
      position="fixed"
      top={0}
      right={0}
      bottom={0}
      left={0}
      _light={{ bg: "gray.50" }}
      _dark={{ bgGradient: "linear(to-b, gray.900, gray.800)" }}
      align="center"
      justify="center"
      zIndex="modal"
    >
      <VStack spacing={8} textAlign="center" px={6} maxW="lg">
        <Stack
          spacing={4}
          align="center"
          _light={{ bg: "white", borderColor: "gray.200" }}
          _dark={{
            bg: "rgba(255,255,255,0.04)",
            borderColor: "whiteAlpha.200",
          }}
          border="1px solid"
          p={{ base: 6, md: 8 }}
          rounded="2xl"
          boxShadow="xl"
          backdropFilter="blur(10px)"
        >
          <Box boxSize={{ base: 20, md: 24 }}>
            <Image src={logo_url} alt={display_name} objectFit="contain" />
          </Box>

          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="semibold"
            _light={{ color: "gray.900" }}
            _dark={{ color: "white" }}
          >
            {heading || `Loading ${display_name}`}
          </Text>
          <Text
            _light={{ color: "gray.600" }}
            _dark={{ color: "whiteAlpha.800" }}
          >
            {message}
          </Text>
          <Text
            fontSize="sm"
            _light={{ color: "gray.500" }}
            _dark={{ color: "whiteAlpha.700" }}
          >
            We'll automatically refresh this page in {secondsRemaining}s.
          </Text>

          {!isError ? (
            <Spinner
              thickness="4px"
              speed="0.75s"
              emptyColor="gray.200"
              color="teal.500"
              size="xl"
            />
          ) : (
            <HStack mt={2} justify="center">
              <Button
                colorScheme="teal"
                variant="solid"
                onClick={onRetry}
                isDisabled={!onRetry}
              >
                Retry loading
              </Button>
              <Button variant="outline" onClick={refreshPage}>
                Refresh now
              </Button>
            </HStack>
          )}
        </Stack>
      </VStack>
    </Flex>
  )
}
